"use client"

import React, { createContext, useContext, useReducer, useCallback, ReactNode, useEffect } from 'react';
import {
  collection, doc, getDoc, setDoc, query, where, getDocs,
  Timestamp, deleteDoc
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '@/lib/firebase';
import { useBudgetManager } from './BudgetManagerContext';
import { ReminderSettings } from '@/types/reminder';

export type CompanySettings = {
  id?: string;
  companyName: string;
  companyAddress: string;
  companyCity: string;
  companyCountry: string;
  postalCode: string;
  companyPhone: string;
  companyEmail: string;
  companyWebsite: string;
  vatNumber?: string;
  registrationNumber?: string;
  logo?: string;
  branding: {
    primaryColor: string;
    accentColor: string;
    fontFamily: string;
  };
  invoiceSettings: {
    prefix: string;
    nextNumber: number;
    terms?: string;
    notes?: string;
    defaultDueDays: number;
    noticeDays: number;
    reminderDays: number[];
    reminderSettings?: ReminderSettings & {
      autoSendReminders: boolean;
      maxReminders: number;
      reminderTemplate?: string;
      noticeTemplate?: string;
    };
  };
  billSettings: {
    terms?: string;
    notes?: string;
    defaultDueDays: number;
    noticeDays: number;
    reminderDays: number[];
    reminderSettings?: ReminderSettings & {
      autoSendReminders: boolean;
      maxReminders: number;
      reminderTemplate?: string;
      noticeTemplate?: string;
    };
  };
  taxSettings?: {
    noticeDays: number;
    reminderDays: number[];
    reminderSettings?: ReminderSettings & {
      autoSendReminders: boolean;
      maxReminders: number;
      reminderTemplate?: string;
      noticeTemplate?: string;
    };
  };
  emailSettings: {
    emailSignature?: string;
    defaultCc?: string[];
    defaultBcc?: string[];
    replyTo?: string;
  };
  social: {
    twitter?: string;
    linkedin?: string;
    facebook?: string;
    instagram?: string;
  };
  budgetId: string;
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
  bankDetails?: string;
};

type CompanyState = {
  settings: CompanySettings | null;
  loading: boolean;
  error: string | null;
};

type CompanyAction =
  | { type: 'SET_SETTINGS'; payload: CompanySettings | null }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'CLEAR_ERROR' };

const defaultSettings = (userId: string, budgetId: string): Omit<CompanySettings, 'id'> => ({
  companyName: '',
  companyAddress: '',
  companyCity: '',
  companyCountry: '',
  postalCode: '',
  companyPhone: '',
  companyEmail: '',
  companyWebsite: '',
  branding: {
    primaryColor: '#007bff',
    accentColor: '#28a745',
    fontFamily: 'Helvetica'
  },
  invoiceSettings: {
    prefix: 'INV',
    nextNumber: 1,
    defaultDueDays: 30,
    noticeDays: 7,
    reminderDays: [-3, -1, 0, 1, 3],
    reminderSettings: {
      autoSendReminders: true,
      maxReminders: 5,
      firstReminder: 1,      // Added
      subsequentReminders: 3, // Added
      reminderTemplate: 'default_invoice_reminder',
      noticeTemplate: 'default_invoice_notice'
    }
  },
  billSettings: {
    defaultDueDays: 30,
    noticeDays: 7,
    reminderDays: [-3, -1, 0, 1, 3],
    reminderSettings: {
      autoSendReminders: true,
      maxReminders: 5,
      firstReminder: 1,      // Added
      subsequentReminders: 3, // Added
      reminderTemplate: 'default_bill_reminder',
      noticeTemplate: 'default_bill_notice'
    }
  },
  taxSettings: {
    noticeDays: 7,
    reminderDays: [-3, -1, 0, 1, 3],
    reminderSettings: {
      autoSendReminders: true,
      maxReminders: 5,
      firstReminder: 1,      // Added
      subsequentReminders: 3, // Added
      reminderTemplate: 'default_tax_reminder',
      noticeTemplate: 'default_tax_notice'
    }
  },
  emailSettings: {},
  social: {},
  budgetId,
  createdAt: new Date(),
  updatedAt: new Date(),
  createdBy: userId
});

type CompanyContextType = {
  state: CompanyState;
  loadSettings: () => Promise<void>;
  updateSettings: (settings: Partial<CompanySettings>) => Promise<void>;
  resetSettings: () => Promise<void>;
  clearError: () => void;
};

const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

const initialState: CompanyState = {
  settings: null,
  loading: false,
  error: null
};

function companyReducer(state: CompanyState, action: CompanyAction): CompanyState {
  switch (action.type) {
    case 'SET_SETTINGS':
      return { ...state, settings: action.payload, loading: false };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    default:
      return state;
  }
}

export function CompanyProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(companyReducer, initialState);
  const { state: budgetState } = useBudgetManager();

  const loadSettings = useCallback(async () => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');

      const currentBudget = budgetState.currentBudget;
      if (!currentBudget?.id) {
        console.warn('No budget selected');
        dispatch({ type: 'SET_SETTINGS', payload: null });
        return;
      }

      // Query company settings for current budget
      const settingsQuery = query(
        collection(db, 'company_settings'),
        where('budgetId', '==', currentBudget.id),
        where('createdBy', '==', user.uid)
      );

      const querySnapshot = await getDocs(settingsQuery);

      if (!querySnapshot.empty) {
        const settingsDoc = querySnapshot.docs[0];
        const data = settingsDoc.data();
        dispatch({
          type: 'SET_SETTINGS',
          payload: {
            ...data,
            id: settingsDoc.id,
            createdAt: data.createdAt.toDate(),
            updatedAt: data.updatedAt.toDate()
          } as CompanySettings
        });
      } else {
        // Initialize with default settings if none exist
        const newSettings = defaultSettings(user.uid, currentBudget.id);
        const docRef = doc(collection(db, 'company_settings'));
        await setDoc(docRef, {
          ...newSettings,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now()
        });

        dispatch({
          type: 'SET_SETTINGS',
          payload: { ...newSettings, id: docRef.id }
        });
      }
    } catch (error: any) {
      console.error('Error loading company settings:', error);
      dispatch({ type: 'SET_ERROR', payload: error.message });
    }
  }, [budgetState.currentBudget]);

  const updateSettings = useCallback(async (newSettings: Partial<CompanySettings>) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');

      const currentBudget = budgetState.currentBudget;
      if (!currentBudget?.id) throw new Error('No budget selected');

      if (!state.settings?.id) throw new Error('No existing settings found');

      const updatedSettings = {
        ...state.settings,
        ...newSettings,
        updatedAt: Timestamp.now()
      };

      await setDoc(doc(db, 'company_settings', state.settings.id), updatedSettings);

      dispatch({
        type: 'SET_SETTINGS',
        payload: {
          ...updatedSettings,
          updatedAt: new Date()
        }
      });
    } catch (error: any) {
      console.error('Error updating company settings:', error);
      dispatch({ type: 'SET_ERROR', payload: error.message });
    }
  }, [budgetState.currentBudget, state.settings]);

  const resetSettings = useCallback(async () => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');

      const currentBudget = budgetState.currentBudget;
      if (!currentBudget?.id) throw new Error('No budget selected');

      // Delete existing settings if they exist
      if (state.settings?.id) {
        await deleteDoc(doc(db, 'company_settings', state.settings.id));
      }

      // Create new settings with defaults
      const newSettings = defaultSettings(user.uid, currentBudget.id);
      const docRef = doc(collection(db, 'company_settings'));
      await setDoc(docRef, {
        ...newSettings,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });

      dispatch({
        type: 'SET_SETTINGS',
        payload: { ...newSettings, id: docRef.id }
      });
    } catch (error: any) {
      console.error('Error resetting company settings:', error);
      dispatch({ type: 'SET_ERROR', payload: error.message });
    }
  }, [budgetState.currentBudget, state.settings]);

  const clearError = useCallback(() => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, []);

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        dispatch({ type: 'SET_SETTINGS', payload: null });
      }
    });

    return () => unsubscribe();
  }, []);

  // Listen for budget changes
  useEffect(() => {
    if (budgetState.currentBudget?.id) {
      loadSettings();
    }
  }, [budgetState.currentBudget?.id, loadSettings]);

  return (
    <CompanyContext.Provider
      value={{
        state,
        loadSettings,
        updateSettings,
        resetSettings,
        clearError
      }}
    >
      {children}
    </CompanyContext.Provider>
  );
}

export function useCompany() {
  const context = useContext(CompanyContext);
  if (!context) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
}